import { ApiError, type TokenProvider } from '../api/client'
import { AuthApiError, type AuthApi, type AuthUser, type CodeSent } from './authApi'
import {
  browserStorage,
  clearSession,
  loadSession,
  parseSession,
  saveSession,
  SESSION_KEY,
  sessionFromTokens,
  type KeyValueStore,
  type Session,
} from './session'

/** The access token is refreshed once fewer than this many milliseconds of it remain. */
export const REFRESH_MARGIN_MS = 60_000

/** How long sign-out waits for the server before ending the session locally anyway. */
export const SIGN_OUT_TIMEOUT_MS = 5_000

export type AuthStatus = 'signedIn' | 'signedOut'

export interface AuthState {
  status: AuthStatus
  user: AuthUser | null
}

const SIGNED_OUT: AuthState = { status: 'signedOut', user: null }

function signedOutError(): ApiError {
  return new ApiError(401, 'You are signed out.')
}

/**
 * Owns the signed-in session: keeps it in memory and in localStorage, hands out access tokens
 * (refreshing them shortly before they expire, one refresh at a time), signs in with an email code
 * and signs out. React reads it through subscribe/getState (useSyncExternalStore).
 */
export class AuthManager implements TokenProvider {
  private readonly api: AuthApi
  private readonly storage: KeyValueStore | null
  private readonly now: () => number
  private readonly listeners = new Set<() => void>()
  private session: Session | null
  private state: AuthState
  private refreshing: Promise<string> | null = null
  private signingOut: Promise<void> | null = null

  constructor(options: { api: AuthApi; storage?: KeyValueStore | null; now?: () => number }) {
    this.api = options.api
    this.storage = options.storage === undefined ? browserStorage() : options.storage
    this.now = options.now ?? (() => Date.now())
    this.session = loadSession(this.storage)
    this.state = this.session ? { status: 'signedIn', user: this.session.user } : SIGNED_OUT
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /** The same object until the signed-in user changes, as useSyncExternalStore requires. */
  readonly getState = (): AuthState => this.state

  /** Asks the API to email a sign-in code. */
  readonly startSignIn = (email: string): Promise<CodeSent> => this.api.start(email.trim())

  /** Exchanges the emailed code for a session and signs in. [deletionCancelled]: this sign-in called off a pending account deletion. */
  readonly verifyCode = async (email: string, code: string): Promise<{ user: AuthUser; deletionCancelled: boolean }> => {
    const tokens = await this.api.verify(email.trim(), code.replace(/\s+/g, ''))
    const session = sessionFromTokens(tokens, this.now())
    this.setSession(session, true)
    return { user: session.user, deletionCancelled: tokens.deletionCancelled }
  }

  readonly getAccessToken = async (): Promise<string> => {
    const session = this.session
    if (!session) throw signedOutError()
    if (session.accessTokenExpiresAt - this.now() > REFRESH_MARGIN_MS) return session.accessToken
    return this.refresh()
  }

  readonly refreshAccessToken = async (rejected: string): Promise<string> => {
    const session = this.session
    if (!session) throw signedOutError()
    // Another request may already have replaced the token the API turned down.
    if (session.accessToken !== rejected && session.accessTokenExpiresAt - this.now() > REFRESH_MARGIN_MS) {
      return session.accessToken
    }
    return this.refresh()
  }

  /** Tells the server (best effort, bounded by SIGN_OUT_TIMEOUT_MS), then forgets the session here. */
  readonly signOut = (): Promise<void> => {
    if (this.signingOut) return this.signingOut
    const session = this.session
    if (!session) {
      this.setSession(null, true)
      return Promise.resolve()
    }
    const run = async () => {
      try {
        await this.api.signOut(session.refreshToken, AbortSignal.timeout(SIGN_OUT_TIMEOUT_MS))
      } catch {
        // Offline or the server failed: the session still ends here, and it lapses on the server.
      } finally {
        if (!this.session || this.session.refreshToken === session.refreshToken) this.setSession(null, true)
        this.signingOut = null
      }
    }
    this.signingOut = run()
    return this.signingOut
  }

  /**
   * Applies a change another tab made to the stored session; wire it to the window 'storage' event.
   * Changes to other keys or to sessionStorage are ignored; clearing all of localStorage signs out.
   */
  readonly handleStorageEvent = (event: Pick<StorageEvent, 'key' | 'newValue' | 'storageArea'>): void => {
    if (!this.storage || event.storageArea !== this.storage) return
    if (event.key !== null && event.key !== SESSION_KEY) return
    this.setSession(event.key === null ? null : parseSession(event.newValue), false)
  }

  /** One refresh at a time: concurrent callers share the request in flight. */
  private refresh(): Promise<string> {
    if (this.refreshing) return this.refreshing
    const started = this.session
    if (!started) return Promise.reject(signedOutError())
    // Signed out, or out and in again (here or in another tab), while the refresh was in flight: the
    // newer state wins over whatever the old session's refresh returned.
    const settled = (current: Session | null): string => {
      if (!current) throw signedOutError()
      return current.accessToken
    }
    const run = async (): Promise<string> => {
      try {
        const tokens = await this.api.refresh(started.refreshToken)
        if (this.session?.refreshToken !== started.refreshToken) return settled(this.session)
        const next = sessionFromTokens(tokens, this.now())
        this.setSession(next, true)
        return next.accessToken
      } catch (error) {
        const current = this.session
        if (current && current.refreshToken !== started.refreshToken) return current.accessToken
        // 401: the server ended the session (signed out elsewhere, expired or unknown).
        if (current && error instanceof AuthApiError && error.status === 401) this.setSession(null, true)
        throw error
      } finally {
        this.refreshing = null
      }
    }
    this.refreshing = run()
    return this.refreshing
  }

  private setSession(next: Session | null, persist: boolean): void {
    this.session = next
    if (persist) {
      if (next) saveSession(this.storage, next)
      else clearSession(this.storage)
    }
    const previous = this.state.user
    const unchanged = next
      ? previous !== null && previous.id === next.user.id && previous.email === next.user.email
      : previous === null
    if (unchanged) return
    this.state = next ? { status: 'signedIn', user: next.user } : SIGNED_OUT
    for (const listener of this.listeners) listener()
  }
}
